import { getSplitsOfTrack } from '@racemap/utilities/functions/event';
import { getShadowtrack } from '@racemap/utilities/functions/map';
import { RacemapGeoTypes, type SplitObject } from '@racemap/utilities/types/geos';
import { Typography } from 'antd';
import type { Immutable } from 'immer';
import type { FC } from 'react';
import type { CurrentEvent } from 'src/store/events/events_reducers';
import type { CheckpointEntry } from '../../BasicComponents/CheckList';
import { MapEditorLink } from '../../BasicComponents/Links';
import { Checkpoint } from './Checkpoint';
import { SpeedTable } from './SpeedTable';

interface Props {
  event: Immutable<CurrentEvent> | null;
}

const { Title } = Typography;

export const SpeedCheckpoint: FC<Props> = ({ event }) => {
  if (event == null) return null;

  const unitType = event.playerOptions.unitType;
  const splits = getSplitsOfTrack(event);
  const spltisWithoutFinish = splits.slice(0, -1);
  const splitsWithDifferentResetSpeed = spltisWithoutFinish.filter((split) =>
    hasSplitResetSpeed(split),
  );
  const splitsWithDefaultMinMaxSpeed = spltisWithoutFinish.filter((split) =>
    isSplitDefaultSpeed(split),
  );

  return (
    <Checkpoint>
      <div style={{ marginBottom: 10 }}>
        <Title level={5}>Segments:</Title>
        Improve the prediction with intermediate timing data from last years. E.g. for a triathlon,
        you can preset splits for swimming, cycling, running, and even for transition.
        <br />
        The prediction assumes that participants do not move backward and do neither move slower nor
        faster then the set limits in each segement. Reads that do not fulfill these conditions are
        not considered for prediction.
        <br />
        Reset: Speed (duration) at which markers are moved from the beginning of a segment until
        correction either by a new read or by the following segment. If there is no reset value
        provided, the dots continue movement from the previous segment.
        <br />
        You have set {splits.length - 1} segment(s).{' '}
        {splitsWithDifferentResetSpeed.length === 0 && (
          <>{splitsWithDifferentResetSpeed.length} segment(s) have a reset speed. </>
        )}
        {splitsWithDefaultMinMaxSpeed.length > 0 && (
          <>
            {splitsWithDefaultMinMaxSpeed.length} segment(s) have only the default minimal and
            maximal segment speed. We <strong>strongly</strong> recommend to set speeds/durations
            that fit to your sport.{' '}
          </>
        )}
        Set splits in the{' '}
        <MapEditorLink eventId={event.id} geoElementType={RacemapGeoTypes.SPLIT} /> in order to add
        segments.
      </div>

      <SpeedTable splits={splits} unitType={unitType} event={event} />
    </Checkpoint>
  );
};

export function getSpeedCheckpointState(
  event: Immutable<CurrentEvent> | null,
): CheckpointEntry['status'] {
  const shadowtrack = getShadowtrack(event);
  const splits = getSplitsOfTrack(event);
  const splitsWithoutFinish = splits.slice(0, -1);
  const splitsWithResetSpeed = splitsWithoutFinish.filter((split) => hasSplitResetSpeed(split));
  const splitsWithDefaultSpeed = splitsWithoutFinish.filter((split) => isSplitDefaultSpeed(split));

  if (splitsWithResetSpeed.length > 0) return 'success';
  if (splitsWithDefaultSpeed.length > 0) return 'warning';
  if (splitsWithResetSpeed.length === 0) return 'primary';
  if (shadowtrack != null) return 'warning';
  return 'danger';
}

const hasSplitResetSpeed = (split: SplitObject): boolean => {
  return split.properties.resetSpeed != null;
};

const isSplitDefaultSpeed = (split: SplitObject): boolean => {
  return split.properties.minSpeed === 0.1 && split.properties.maxSpeed === 360;
};
